import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Navigate, useLocation } from "react-router-dom";

import {
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  TableFooter,
  TablePagination,
  Tab,
  Tabs,
  useTheme,
  Typography,
} from "@mui/material";
import {
  Chart,
  ArgumentAxis,
  ValueAxis,
  BarSeries,
  Legend,
  LineSeries,
  Title,
} from "@devexpress/dx-react-chart-material-ui";

import { ArgumentScale, Stack, Animation } from "@devexpress/dx-react-chart";
import { scaleBand } from "@devexpress/dx-chart-core";
import SwipeableViews from "react-swipeable-views";
import CountUp from "react-countup";

import * as actions from "../../redux/actions/index";
import { getRandomColor } from "../../utils/getRandomColor";

import Filter from "../../components/Filter/Filter.component";
import PageLoading from "../../components/PageLoading/PageLoading.component";

import { TabPanel } from "../../components/TabPanel/TabPanel.component";
import { getSessionStorageItem } from "../../utils/getSessionStorageItem";
import { getStoredParams } from "../../utils/getStoredParams";
import { useLoadingAndResetRefs } from "../../utils/useLoadingAndResetRefs";
import { summayPerBrand, summaryPerSku } from "../../utils/generateSummary";
import { createSortHandler } from "../../utils/applySort";
import { getPaginatedData, getDataCount } from "../../utils/getPaginatedData";

const PAGE_SIZE_OPTIONS = [20];
const FIRST_PAGE = 10;

const Dashboard = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const theme = useTheme();
  const cmsAuth = useSelector((state) => state.cmsAuthenticator.cmsAuth || "");
  const products = useSelector((state) => state.cmsProducts.products);
  const datas = useSelector((state) => state.cmsData.datas || []);
  const loading = useSelector((state) => state.cmsData.fetchDatasLoading);
  const filtersLoading = useSelector((state) => state.cmsData.filtersLoading);
  const filtersApplied = useSelector((state) => state.cmsData.filtersApplied);
  const filtersReset = useSelector((state) => state.cmsData.filtersReset);

  const [skuPage, setSkuPage] = useState(0);
  const [skuRowsPerPage, setSkuRowsPerPage] = useState(FIRST_PAGE);
  const [brandsPage, setBrandsPage] = useState(0);
  const [brandsRowsPerPage, setBrandsRowsPerPage] = useState(FIRST_PAGE);
  const [tab, setTab] = useState(0);
  const [transactionPerDayState, setTransactionPerDayState] = useState([]);
  const [dailyItemsState, setDailyItemsState] = useState([]);
  const [sortBrand, setSortBrand] = useState("");
  const [sortSku, setSortSku] = useState("");
  const [order, setOrder] = useState("-");

  let displayedProductCount = 0;

  const agentRestrict =
    cmsAuth.role === "AGENT" ? <Navigate to="/transactions" /> : null;

  const storedParams = getStoredParams(location, getSessionStorageItem);

  const { isLoadingRef, isFiltersReset } = useLoadingAndResetRefs(
    loading,
    filtersReset
  );

  useEffect(() => {
    const fetchData = (params) => {
      const sortParamsBrand = sortBrand ? `${order}${sortBrand}` : null;
      const sortParamsSku = sortSku ? `${order}${sortSku}` : null;

      dispatch(
        actions.onDashboardDatas(params, sortParamsBrand, sortParamsSku)
      );
    };

    if (filtersApplied && storedParams && !isLoadingRef.current) {
      fetchData(storedParams);
    } else if (!filtersApplied && !filtersLoading && !isFiltersReset.current) {
      fetchData(storedParams || null);
    }
  }, [
    dispatch,
    isLoadingRef,
    isFiltersReset,
    filtersApplied,
    filtersLoading,
    storedParams,
    sortBrand,
    sortSku,
    order,
  ]);

  useEffect(() => {
    if (datas && datas.transactionsPerDay) {
      const transactionPerDay = datas.transactionsPerDay.slice(
        Math.max(datas.transactionsPerDay.length - 7, 0)
      );
      setTransactionPerDayState(transactionPerDay);
    }
  }, [datas]);

  useEffect(() => {
    dispatch(actions.onFetchProducts());
  }, [dispatch]);

  useEffect(() => {
    if (datas && datas.transactionsPerDay) {
      const transactionPerDay = datas.transactionsPerDay.slice(
        Math.max(datas.transactionsPerDay.length - 7, 0)
      );
      setTransactionPerDayState(transactionPerDay);
    }
  }, [datas]);

  useEffect(() => {
    if (datas && datas.dailyItems) {
      const itemsPerDay = datas.dailyItems.slice(
        Math.max(datas.dailyItems.length - 7, 0)
      );
      setDailyItemsState(itemsPerDay);
    }
  }, [datas]);

  // Get product name from product ID
  const getProductName = (productId) => {
    const product =
      products && products.find((product) => product.id === productId);
    return product ? product.name : "";
  };

  // PAGINATION
  const paginatedSummaryBrand = getPaginatedData(
    datas.detailedBrands,
    brandsPage,
    brandsRowsPerPage
  );

  const paginatedSummarySku = getPaginatedData(
    datas.detailedItems,
    skuPage,
    skuRowsPerPage
  );
  const summarySkuCount = getDataCount(datas.detailedItems);
  const summaryBrandCount = getDataCount(datas.detailedBrands);

  const handleChangeSkuPage = (event, newPage) => {
    setSkuPage(newPage);
  };
  const handleChangeSkuRowsPerPage = (event) => {
    setSkuRowsPerPage(parseInt(event.target.value, 10));
    setSkuPage(0);
  };

  const handleChangeBrandPage = (event, newPage) => {
    setBrandsPage(newPage);
  };
  const handleChangeBrandRowsPerPage = (event) => {
    setBrandsRowsPerPage(parseInt(event.target.value, 10));
    setBrandsPage(0);
  };

  // Handle sort for Brand
  const handleSortBrand = (cellId) =>
    createSortHandler(cellId, setSortBrand, sortBrand, order, setOrder);

  // Handle sort for SKU
  const handleSortSku = (cellId) =>
    createSortHandler(cellId, setSortSku, sortSku, order, setOrder);

  return (
    <>
      {agentRestrict}
      <Filter page="dashboard" />
      <PageLoading loading={loading} />
      <Paper style={{ width: "80%", margin: "0 auto" }}>
        <Tabs
          indicatorColor="primary"
          value={tab}
          onChange={(e, v) => setTab(v)}
          variant="fullWidth"
        >
          <Tab label="Transactions" />
          <Tab label="Products" />
          <Tab label="Ads" />
        </Tabs>
      </Paper>

      <SwipeableViews
        axis={theme.direction === "rtl" ? "x-reverse" : "x"}
        index={tab}
        onChange={(i) => setTab(i)}
      >
        <TabPanel value={tab} index={0} dir={theme.direction}>
          <div className="Dashboard">
            <div className="Dashboard__container--items">
              <Paper elevation={3} className="Dashboard__container--item">
                <h3 className="heading-secondary"> Total Transactions </h3>
                <CountUp
                  end={datas.totalTransaction}
                  separator={" "}
                  duration={1.4}
                  className="heading-countup"
                />
              </Paper>
              <Paper elevation={3} className="Dashboard__container--item">
                <h3 className="heading-secondary">Successful Transactions</h3>
                <CountUp
                  end={datas.successfulTransactions}
                  separator={" "}
                  duration={1.4}
                  className="heading-countup"
                />
              </Paper>
              <Paper elevation={3} className="Dashboard__container--item">
                <h3 className="heading-secondary"> Successful Uploaders </h3>
                <CountUp
                  end={datas.successfulUploaders}
                  separator={" "}
                  duration={1.4}
                  className="heading-countup"
                />
              </Paper>
              <Paper elevation={3} className="Dashboard__container--item">
                <h3 className="heading-secondary"> Repeat Buyers </h3>
                <CountUp
                  end={datas.multipleUploaders}
                  separator={" "}
                  duration={1.4}
                  className="heading-countup"
                />
              </Paper>
              <Paper elevation={3} className="Dashboard__container--item">
                <h3 className="heading-secondary"> Daily Transactions </h3>
                <CountUp
                  end={datas.averageTransaction}
                  separator={" "}
                  duration={1.4}
                  className="heading-countup"
                />
              </Paper>
              <Paper elevation={3} className="Dashboard__container--item">
                <h3 className="heading-secondary">Fully Registered</h3>
                <CountUp
                  end={datas.numberOfRegistrations}
                  separator={","}
                  duration={1.4}
                  className="heading-countup"
                />
              </Paper>
            </div>
          </div>

          <Paper elevation={3} className="Dashboard__container--chart-1">
            {datas.transactionsPerDay && (
              <Chart data={transactionPerDayState} height={370} width={"50%"}>
                <ArgumentAxis />
                <ValueAxis />
                <Animation />
                <Title text="Total Transactions Per Week" />

                <LineSeries
                  className="Dashboard__container--chart-1"
                  valueField="count"
                  argumentField="date"
                  name="Transactions Per Day"
                />
              </Chart>
            )}
          </Paper>

          {/* GCASH */}
          <div className="Dashboard" style={{ marginTop: "20px" }}>
            <div className="Dashboard__container--items">
              <Paper elevation={3} className="Dashboard__container--item">
                <h3 className="heading-secondary"> GCash Wallet balance </h3>

                <CountUp
                  end={
                    datas.gCashBalance && datas.gCashBalance[0].availableBalance
                  }
                  prefix={"₱ "}
                  separator={","}
                  duration={1.4}
                  className="heading-countup"
                />
              </Paper>
            </div>
          </div>

          {/* COMMON INVALID REASONS */}
          <Typography
            align="center"
            variant="h4"
            style={{ margin: "2rem 0 1.2rem" }}
          >
            Common Invalid Reasons
          </Typography>
          <TableContainer
            component={Paper}
            elavaton={2}
            style={{ width: "80%", margin: "1rem auto" }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="center">Reason</TableCell>
                  <TableCell align="center">Invalid Count</TableCell>
                  <TableCell align="center">Experiencers</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {datas.commonInvalidReasons &&
                  datas.commonInvalidReasons.map((item, i) => (
                    <TableRow key={i}>
                      <TableCell align="center" width={"300px"}>
                        {item.reason}
                      </TableCell>
                      <TableCell align="center" width={"300px"}>
                        {item.invalid_count}
                      </TableCell>
                      <TableCell align="center" width={"300px"}>
                        {item.experiencers}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Number of Transactions Per Branch */}
          <Typography
            align="center"
            variant="h4"
            style={{ margin: "2rem 0 1.2rem" }}
          >
            Number of Transactions Per Branch
          </Typography>
          <TableContainer
            component={Paper}
            elavaton={2}
            style={{ width: "80%", margin: "1rem auto" }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="center">Branch</TableCell>
                  <TableCell align="center">Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {datas.numberOfTransactionsPerBranch &&
                  datas.numberOfTransactionsPerBranch.map((item, i) => (
                    <TableRow key={i}>
                      <TableCell align="center" width={"300px"}>
                        {item.branch}
                      </TableCell>
                      <TableCell align="center" width={"300px"}>
                        {item.count}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
        <TabPanel value={tab} index={1} dir={theme.direction}>
          <div className="Dashboard">
            <Paper elevation={3} className="Dashboard__container--chart">
              <Chart data={dailyItemsState} height={"200px"} width={"100%"}>
                <ArgumentScale factory={scaleBand} />
                <ArgumentAxis />
                <ValueAxis />

                {datas &&
                  datas.dailyItems &&
                  dailyItemsState.length > 0 &&
                  Object.keys(datas.dailyItems[0] || {}).map((key, index) => {
                    if (key.startsWith("p_")) {
                      if (displayedProductCount >= 20) return null;
                      displayedProductCount++;

                      const productId = parseInt(key.split("_")[1]);
                      const productName = getProductName(productId);

                      if (!productName) return null;

                      return (
                        <BarSeries
                          key={index}
                          valueField={key}
                          argumentField="day"
                          name={productName}
                          color={getRandomColor()}
                        />
                      );
                    }
                    return null;
                  })}

                <Stack />
                <Legend />
                <Animation />
                <Title text="Total Products Bought Per Week" />
              </Chart>
            </Paper>

            <Typography
              align="center"
              variant="h4"
              style={{ margin: "2rem 0 1.2rem" }}
            >
              Summary Per Brand
            </Typography>

            <TableContainer
              component={Paper}
              elevation={2}
              style={{ margin: "1rem auto" }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    {summayPerBrand.map((headCell) => (
                      <TableCell
                        key={headCell.id}
                        sortDirection={
                          sortBrand === headCell.id
                            ? order === "-"
                              ? "desc"
                              : "asc"
                            : false
                        }
                        width="100px"
                        align="center"
                      >
                        {headCell.sortable ? (
                          <TableSortLabel
                            active={sortBrand === headCell.id}
                            direction={
                              sortBrand === headCell.id
                                ? order === "-"
                                  ? "desc"
                                  : "asc"
                                : "asc"
                            }
                            onClick={() => handleSortBrand(headCell.id)}
                          >
                            {headCell.label}
                          </TableSortLabel>
                        ) : (
                          headCell.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedSummaryBrand.map((item, i) => (
                    <TableRow key={i}>
                      <TableCell align="center">{item.product_name}</TableCell>
                      <TableCell align="center">{item.total}</TableCell>
                      <TableCell align="center">
                        {new Intl.NumberFormat("ph-PH", {
                          style: "currency",
                          currency: "PHP",
                        }).format(item.peso_value)}
                      </TableCell>
                      <TableCell align="center">{item.uploaders}</TableCell>
                      <TableCell align="center">{item.repeat}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                      colSpan={5}
                      count={summaryBrandCount}
                      rowsPerPage={brandsRowsPerPage}
                      page={brandsPage}
                      SelectProps={{
                        inputProps: {
                          "aria-label": "rows per page",
                        },
                        native: true,
                      }}
                      onPageChange={handleChangeBrandPage}
                      onRowsPerPageChange={handleChangeBrandRowsPerPage}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>

            <Typography
              align="center"
              variant="h4"
              style={{ margin: "2rem 0 1.2rem" }}
            >
              Summary Per SKU
            </Typography>
            <TableContainer
              component={Paper}
              elavaton={2}
              style={{ margin: "1rem auto" }}
            >
              <Table>
                <TableHead>
                  {summaryPerSku.map((headCell) => (
                    <TableCell
                      key={headCell.id}
                      sortDirection={
                        sortSku === headCell.id
                          ? order === "-"
                            ? "desc"
                            : "asc"
                          : false
                      }
                      width="100px"
                      align="center"
                    >
                      {headCell.sortable ? (
                        <TableSortLabel
                          active={sortSku === headCell.id}
                          direction={
                            sortSku === headCell.id
                              ? order === "-"
                                ? "desc"
                                : "asc"
                              : "asc"
                          }
                          onClick={() => handleSortSku(headCell.id)}
                        >
                          {headCell.label}
                        </TableSortLabel>
                      ) : (
                        headCell.label
                      )}
                    </TableCell>
                  ))}
                </TableHead>
                <TableBody>
                  {paginatedSummarySku.map((item, i) => (
                    <TableRow key={i}>
                      <TableCell>{item.product_name}</TableCell>
                      <TableCell align="center">{item.total}</TableCell>
                      <TableCell align="center">
                        {new Intl.NumberFormat("ph-PH", {
                          style: "currency",
                          currency: "PHP",
                        }).format(item.peso_value)}
                      </TableCell>
                      <TableCell align="center">{item.uploaders}</TableCell>
                      <TableCell align="center">{item.repeat}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                      colSpan={5}
                      count={summarySkuCount}
                      rowsPerPage={skuRowsPerPage}
                      page={skuPage}
                      SelectProps={{
                        inputProps: {
                          "aria-label": "rows per page",
                        },
                        native: true,
                      }}
                      onPageChange={handleChangeSkuPage}
                      onRowsPerPageChange={handleChangeSkuRowsPerPage}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </div>
        </TabPanel>

        {/* Adclicks Per Ref */}
        <TabPanel value={tab} index={2} dir={theme.direction}>
          <Typography
            align="center"
            variant="h4"
            style={{ margin: "2rem 0 1.2rem" }}
          >
            AdClicks Per Ref
          </Typography>
          <TableContainer
            component={Paper}
            elavaton={2}
            style={{ width: "80%", margin: "1rem auto" }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="center">Ref</TableCell>
                  <TableCell align="center">Clicks</TableCell>
                  <TableCell align="center">Fully Registered</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {datas.adclicksPerRef &&
                  datas.adclicksPerRef.map((item, i) => (
                    <TableRow key={i}>
                      <TableCell align="center" width={"300px"}>
                        {item.ref}
                      </TableCell>
                      <TableCell align="center" width={"300px"}>
                        {item.clicks}
                      </TableCell>
                      <TableCell align="center" width={"300px"}>
                        {item.fully_registered}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </SwipeableViews>
    </>
  );
};

export default Dashboard;
